/**
 * Modern Cart Page Handler
 * Handles quantity updates, item removal, and cart interactions
 */

(function () {
  'use strict';

  console.log('[Cart Modern] Script loaded');

  class CartModern {
    constructor() {
      console.log('[Cart Modern] Initializing...');

      this.cart = document.querySelector('.cart-modern');
      if (!this.cart) {
        console.error('[Cart Modern] .cart-modern element not found');
        return;
      }

      this.form = this.cart.querySelector('#cart');
      this.items = this.cart.querySelector('[data-cart-items]');

      console.log('[Cart Modern] Elements:', {
        cart: !!this.cart,
        form: !!this.form,
        items: !!this.items,
      });

      if (!this.items) {
        console.error('[Cart Modern] [data-cart-items] not found');
        return;
      }

      this.init();
    }

    init() {
      console.log('[Cart Modern] Attaching event listeners...');
      this.attachEventListeners();

      // Load recommendations
      const recommendationsContainer = this.cart.querySelector('[data-recommendations-container]');
      if (recommendationsContainer && recommendationsContainer.children.length === 0) {
        this.loadRecommendations();
      }

      console.log('[Cart Modern] Initialization complete');
    }

    attachEventListeners() {
      // Event delegation for better performance and dynamic content handling
      this.items.addEventListener('click', async (e) => {
        const target = e.target;

        // Check for quantity decrease button
        const decreaseBtn = target.closest('[data-quantity-decrease]');
        if (decreaseBtn) {
          e.preventDefault();
          e.stopPropagation();
          console.log('[Cart Modern] Decrease button clicked');
          const item = decreaseBtn.closest('[data-cart-item]');
          const input = item.querySelector('[data-quantity-input]');
          const currentQty = parseInt(input.value) || 1;
          const newQty = Math.max(0, currentQty - 1);
          this.updateQuantity(item, newQty);
          return;
        }

        // Check for quantity increase button
        const increaseBtn = target.closest('[data-quantity-increase]');
        if (increaseBtn) {
          e.preventDefault();
          e.stopPropagation();
          console.log('[Cart Modern] Increase button clicked');
          const item = increaseBtn.closest('[data-cart-item]');
          const input = item.querySelector('[data-quantity-input]');
          const currentQty = parseInt(input.value) || 0;
          const newQty = currentQty + 1;
          this.updateQuantity(item, newQty);
          return;
        }

        // Check for remove button
        const removeBtn = target.closest('[data-remove-item]');
        if (removeBtn) {
          e.preventDefault();
          e.stopPropagation();
          console.log('[Cart Modern] Remove button clicked');
          const item = removeBtn.closest('[data-cart-item]');
          this.updateQuantity(item, 0);
          return;
        }

        // Check for discount remove button
        const removeDiscountBtn = target.closest('[data-remove-discount]');
        if (removeDiscountBtn) {
          e.preventDefault();
          e.stopPropagation();
          const discountCode = removeDiscountBtn.dataset.removeDiscount;
          console.log('[Cart Modern] Removing discount:', discountCode);
          await this.removeDiscount(discountCode);
          return;
        }
      });

      // Quantity input change
      this.items.addEventListener('change', (e) => {
        if (e.target.matches('[data-quantity-input]')) {
          console.log('[Cart Modern] Quantity input changed');
          const item = e.target.closest('[data-cart-item]');
          const newQty = Math.max(0, parseInt(e.target.value) || 0);
          this.updateQuantity(item, newQty);
        }
      });

      // Discount form
      const discountForm = this.cart.querySelector('[data-discount-form]');
      if (discountForm) {
        discountForm.addEventListener('submit', (e) => this.handleDiscountSubmit(e));
      }

      // Agreement checkbox
      const agreementCheckbox = this.cart.querySelector('[data-agreement-checkbox]');
      const checkoutButton = this.cart.querySelector('[data-checkout-button]');
      if (agreementCheckbox && checkoutButton) {
        agreementCheckbox.addEventListener('change', (e) => {
          checkoutButton.disabled = !e.target.checked;
        });
      }

      // Cart note auto-save
      const noteInput = this.form?.querySelector('#CartNote');
      if (noteInput) {
        let noteTimeout;
        noteInput.addEventListener('input', () => {
          clearTimeout(noteTimeout);
          noteTimeout = setTimeout(() => this.updateNote(noteInput.value), 500);
        });
      }

      console.log('[Cart Modern] Event listeners attached');
    }

    async updateQuantity(itemElement, quantity) {
      const variantId = itemElement.dataset.variantId;
      const input = itemElement.querySelector('[data-quantity-input]');
      const display = itemElement.querySelector('.quantity__display');

      console.log('[Cart Modern] Updating quantity:', { variantId, quantity });

      if (!variantId) {
        console.error('[Cart Modern] No variant ID found');
        return;
      }

      // Check for max quantity limit (if previously set from stock error)
      const maxQty = itemElement.dataset.maxQuantity;
      if (maxQty && quantity > parseInt(maxQty)) {
        this.showError(`You can only order maximum ${maxQty} of this product.`);
        // Revert to max quantity
        if (input) input.value = maxQty;
        if (display) display.textContent = maxQty;
        return;
      }

      // Update UI optimistically
      if (input) {
        input.value = quantity;
      }
      if (display) {
        display.textContent = quantity;
      }

      // Add loading state
      itemElement.classList.add('is-updating');
      if (quantity === 0) {
        itemElement.classList.add('is-removing');
      }

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity,
          }),
        });

        if (!response.ok) {
          // Handle stock limit errors (422) FIRST
          if (response.status === 422) {
            const errorData = await response.json().catch(() => null);

            // Parse the stock limit from the message (both English and Turkish formats)
            const matchEnglish = errorData?.message?.match(/Only (\d+) item/);
            const matchGeneric = errorData?.message?.match(/(\d+)/);
            const stockQty = matchEnglish ? parseInt(matchEnglish[1]) : matchGeneric ? parseInt(matchGeneric[1]) : null;

            // Store max quantity for this item to prevent future attempts
            if (stockQty !== null) {
              itemElement.dataset.maxQuantity = stockQty;
              this.showError(`You can only order maximum ${stockQty} of this product.`);

              // Revert the display to max quantity
              if (display) display.textContent = stockQty;
              if (input) input.value = stockQty;
            } else {
              // No specific quantity found, show general stock error
              this.showError('Unable to update quantity due to stock limit.');
            }

            // Remove loading state
            itemElement.classList.remove('is-updating', 'is-removing');
            return;
          }

          // Other errors
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
        }

        const cart = await response.json();
        console.log('[Cart Modern] Cart updated:', cart);

        if (quantity === 0) {
          // Remove item with animation
          itemElement.style.transform = 'translateX(-100%)';
          itemElement.style.opacity = '0';
          setTimeout(() => {
            itemElement.remove();
            this.checkEmptyCart();
          }, 300);
        } else {
          itemElement.classList.remove('is-updating');
          // Update price display
          const lineItem = cart.items.find((item) => item.variant_id == variantId);
          if (lineItem) {
            this.updateItemPrice(itemElement, lineItem);
          }
        }

        // Update totals
        this.updateTotals(cart);

        // Update cart count
        this.updateCartCount(cart.item_count);
      } catch (error) {
        console.error('[Cart Modern] Error updating quantity:', error);
        itemElement.classList.remove('is-updating', 'is-removing');

        // Show user-friendly error message
        const errorMessage = error.message.includes('HTTP error')
          ? 'Failed to update cart. Please try again.'
          : error.message;

        this.showError(errorMessage);

        // Revert input value - fetch current cart to get accurate quantity
        try {
          const cartResponse = await fetch('/cart.js', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
            },
          });

          if (cartResponse.ok) {
            const cart = await cartResponse.json();
            const currentItem = cart.items.find((item) => item.variant_id == variantId);
            if (currentItem) {
              if (input) input.value = currentItem.quantity;
              if (display) display.textContent = currentItem.quantity;
            }
          }
        } catch (revertError) {
          console.error('[Cart Modern] Failed to revert quantity:', revertError);
          // Fallback to original value
          if (input) {
            const originalValue = input.dataset.originalValue || 1;
            input.value = originalValue;
            if (display) display.textContent = originalValue;
          }
        }
      }
    }

    updateItemPrice(itemElement, lineItem) {
      const priceContainer = itemElement.querySelector('.cart-modern__item-price');
      if (!priceContainer) return;

      const currency = window.Shopify?.currency?.active || 'USD';
      const formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
      });

      const priceHtml =
        lineItem.original_line_price !== lineItem.final_line_price
          ? `<span class="cart-modern__item-price--original">${formatter.format(
              lineItem.original_line_price / 100
            )}</span>
         <span class="cart-modern__item-price--sale">${formatter.format(lineItem.final_line_price / 100)}</span>`
          : `<span class="cart-modern__item-price--regular">${formatter.format(
              lineItem.original_line_price / 100
            )}</span>`;

      priceContainer.innerHTML = priceHtml;
    }

    updateTotals(cart) {
      const currency = window.Shopify?.currency?.active || 'USD';
      const formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
      });

      const subtotal = this.cart.querySelector('[data-cart-subtotal]');
      const total = this.cart.querySelector('[data-cart-total]');

      if (subtotal) {
        subtotal.textContent = formatter.format(cart.total_price / 100);
      }

      if (total) {
        total.textContent = formatter.format(cart.total_price / 100);
      }
    }

    updateCartCount(count) {
      const cartCounts = document.querySelectorAll('[data-cart-count]');
      cartCounts.forEach((el) => {
        el.textContent = count;
        el.style.display = count > 0 ? 'inline-flex' : 'none';
      });

      // Update page title
      const titleElement = this.cart.querySelector('.cart-modern__title');
      if (titleElement) {
        titleElement.textContent = (window.cartStrings?.titleWithCount || 'Your cart ([count])').replace('[count]', count);
      }
    }

    checkEmptyCart() {
      const items = this.items?.querySelectorAll('[data-cart-item]');
      if (!items || items.length === 0) {
        // Reload page to show empty cart state
        window.location.reload();
      }
    }

    async handleDiscountSubmit(event) {
      event.preventDefault();

      const form = event.target;
      const input = form.querySelector('input[name="discount"]');
      const button = form.querySelector('button');
      const code = input.value.trim();

      if (!code) {
        this.showDiscountMessage(window.cartStrings?.enterDiscountCode || 'Please enter a discount code', 'error');
        return;
      }

      button.disabled = true;
      const originalText = button.textContent;
      button.textContent = window.cartStrings?.applyingDiscount || 'Applying...';

      try {
        console.log('[Cart Modern] Applying discount code:', code);

        // Apply discount via AJAX
        const formData = new FormData();
        formData.append('discount', code);

        const response = await fetch('/cart', {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          // Fetch updated cart
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();

          console.log('[Cart Modern] Cart after discount:', cart);

          // Check if discount was applied
          const discountApplied =
            cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0;

          if (discountApplied) {
            this.showDiscountMessage(window.cartStrings?.discountAppliedSuccess || 'Discount applied successfully!', 'success');
            input.value = '';

            // Update cart display
            this.updateCartAfterDiscount(cart);
          } else {
            this.showDiscountMessage(window.cartStrings?.invalidDiscount || 'Invalid discount code', 'error');
          }
        } else {
          this.showDiscountMessage(window.cartStrings?.invalidDiscount || 'Invalid discount code', 'error');
        }
      } catch (error) {
        console.error('[Cart Modern] Discount error:', error);
        this.showDiscountMessage(window.cartStrings?.applyDiscountError || 'Error applying discount', 'error');
      } finally {
        button.disabled = false;
        button.textContent = originalText;
      }
    }

    updateCartAfterDiscount(cart) {
      console.log('[Cart Modern] Updating cart display after discount');

      // Update subtotal (original total)
      const subtotal = this.cart.querySelector('[data-cart-subtotal]');
      if (subtotal) {
        const currency = window.Shopify?.currency?.active || 'USD';
        const formatter = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency,
        });
        subtotal.textContent = formatter.format(cart.original_total_price / 100);
      }

      // Update discounts section
      const discountsContainer = this.cart.querySelector('[data-cart-discounts]');
      // Update discounts using Shopify's discount_codes field
      if (discountsContainer && cart.discount_codes) {
        const currency = window.Shopify?.currency?.active || 'USD';
        const formatter = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency,
        });

        const applicableDiscounts = cart.discount_codes.filter((d) => d.applicable);

        if (applicableDiscounts.length > 0) {
          const percentage =
            cart.total_discount && cart.original_total_price
              ? Math.round((cart.total_discount / cart.original_total_price) * 100)
              : 0;

          discountsContainer.innerHTML = applicableDiscounts
            .map((discount) => {
              return `
                <div class="cart-modern__discount-item" data-discount-code="${this.escapeHtml(discount.code)}">
                  <span class="cart-modern__discount-title">
                    <span class="discount-code-name">${this.escapeHtml(discount.code.toUpperCase())}</span>
                    <span class="cart-modern__discount-percentage">(-${percentage}%)</span>
                  </span>
                  <span class="cart-modern__discount-amount">-${formatter.format(cart.total_discount / 100)}</span>
                </div>
              `;
            })
            .join('');
        } else if (cart.total_discount && cart.total_discount > 0) {
          // Product/automatic discount
          const percentage = cart.original_total_price
            ? Math.round((cart.total_discount / cart.original_total_price) * 100)
            : 0;

          discountsContainer.innerHTML = `
            <div class="cart-modern__discount-item">
              <span class="cart-modern__discount-title">
                <span class="discount-code-name">${this.escapeHtml(window.cartStrings?.productDiscount || 'Product Discount')}</span>
                <span class="cart-modern__discount-percentage">(-${percentage}%)</span>
              </span>
              <span class="cart-modern__discount-amount">-${formatter.format(cart.total_discount / 100)}</span>
            </div>
          `;
        }
      }

      // Update total
      this.updateTotals(cart);

      // Update applied discount tags
      this.updateAppliedDiscountTags(cart);
    }

    /**
     * Update applied discount tags dynamically
     */
    updateAppliedDiscountTags(cart) {
      const tagsContainer = this.cart.querySelector('[data-applied-discounts]');
      if (!tagsContainer) return;

      // Get applicable discount codes
      const applicableDiscounts = cart.discount_codes?.filter((code) => code.applicable) || [];

      if (applicableDiscounts.length === 0) {
        tagsContainer.innerHTML = '';
        return;
      }

      // Render discount tags
      tagsContainer.innerHTML = applicableDiscounts
        .map((discount) => {
          return `
            <div class="discount-tag" data-discount-tag="${this.escapeHtml(discount.code)}">
              <span class="discount-tag__code">${this.escapeHtml(discount.code.toUpperCase())}</span>
              <button
                type="button"
                class="discount-tag__remove"
                data-remove-discount="${this.escapeHtml(discount.code)}"
                aria-label="${this.escapeHtml(window.cartStrings?.removeDiscount || 'Remove')} ${this.escapeHtml(discount.code)}"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M8 2L2 8M2 2L8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
          `;
        })
        .join('');
    }

    /**
     * Remove discount code using Shopify Cart API
     * Updates cart without page reload
     */
    async removeDiscount(discountCode) {
      try {
        console.log('[Cart Modern] Removing discount:', discountCode);

        // Show loading state
        const tagsContainer = this.cart.querySelector('[data-applied-discounts]');
        if (tagsContainer) {
          tagsContainer.style.opacity = '0.5';
          tagsContainer.style.pointerEvents = 'none';
        }

        // Use Shopify's Cart API to clear discount
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            discount: '', // Empty string clears all discounts
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to remove discount');
        }

        // Fetch fresh cart data
        const cartResponse = await fetch('/cart.js', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const cart = await cartResponse.json();
        console.log('[Cart Modern] Discount removed, cart updated:', cart);

        // Update cart UI without page reload
        this.updateCartAfterDiscount(cart);

        // Show success message
        this.showDiscountMessage(window.cartStrings?.discountRemoved || 'Discount code removed', 'success');
      } catch (error) {
        console.error('[Cart Modern] Failed to remove discount:', error);

        // Reset loading state
        const tagsContainer = this.cart.querySelector('[data-applied-discounts]');
        if (tagsContainer) {
          tagsContainer.style.opacity = '1';
          tagsContainer.style.pointerEvents = 'auto';
        }

        this.showError(window.cartStrings?.removeDiscountError || 'Failed to remove discount. Please try again.');
      }
    }

    escapeHtml(text) {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      };
      return text.replace(/[&<>"']/g, (m) => map[m]);
    }

    showDiscountMessage(message, type) {
      const messagesContainer = this.cart.querySelector('[data-discount-messages]');
      if (!messagesContainer) return;

      messagesContainer.textContent = message;
      messagesContainer.className = `cart-modern__discount-messages ${type}`;

      setTimeout(() => {
        messagesContainer.textContent = '';
        messagesContainer.className = 'cart-modern__discount-messages';
      }, 3000);
    }

    showError(message) {
      // Create a modern error notification
      const existingError = this.cart.querySelector('.cart-modern__error-message');
      if (existingError) {
        existingError.remove();
      }

      const errorDiv = document.createElement('div');
      errorDiv.className = 'cart-modern__error-message';
      errorDiv.setAttribute('role', 'alert');
      errorDiv.innerHTML = `
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM10 6v4M10 14h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>${this.escapeHtml(message)}</span>
        <button type="button" class="error-close" aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      `;

      // Insert at the top of cart
      const cartHeader = this.cart.querySelector('.cart-modern__header') || this.cart.firstElementChild;
      if (cartHeader && cartHeader.parentNode) {
        cartHeader.parentNode.insertBefore(errorDiv, cartHeader.nextSibling);
      } else {
        this.cart.prepend(errorDiv);
      }

      // Close button handler
      const closeBtn = errorDiv.querySelector('.error-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => errorDiv.remove());
      }

      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (errorDiv && errorDiv.parentNode) {
          errorDiv.remove();
        }
      }, 5000);

      console.error('[Cart Modern]', message);
    }

    async updateNote(note) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ note }),
        });
        console.log('[Cart Modern] Note updated');
      } catch (error) {
        console.error('[Cart Modern] Error updating cart note:', error);
      }
    }

    async loadRecommendations() {
      const recommendationsContainer = this.cart.querySelector('[data-recommendations-container]');
      if (!recommendationsContainer) return;

      try {
        const firstItem = this.items?.querySelector('[data-cart-item]');
        if (!firstItem) return;

        const productId = firstItem.dataset.productId;
        if (!productId) {
          console.warn('[Cart Modern] No product ID found for recommendations');
          return;
        }

        console.log('[Cart Modern] Loading recommendations for product ID:', productId);

        const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=4`);
        if (!response.ok) {
          console.error('[Cart Modern] Recommendations API response not OK:', response.status);
          return;
        }

        const data = await response.json();

        if (!data.products || data.products.length === 0) {
          console.log('[Cart Modern] No recommendations found');
          return;
        }

        // Note: Recommendations rendering would be done here
        // For now, we'll leave it to Liquid templating
        console.log('[Cart Modern] Recommendations loaded:', data.products.length);
      } catch (error) {
        console.error('[Cart Modern] Error loading recommendations:', error);
      }
    }
  }

  // Initialize cart when DOM is ready
  function init() {
    console.log('[Cart Modern] DOM ready, initializing cart...');
    new CartModern();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
